import React, { useState, useEffect, useCallback } from 'react';
import { feedbackAPI } from '../../../services/api';
import './AdminSections.css';

function FeedbackManagement() {
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  // Fetch feedback statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await feedbackAPI.getStats();
      // Handle response properly - check if it's wrapped in data
      const statsData = response.data || response;
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching stats:', err);
      // Set default stats if fetch fails
      setStats({ total: 0, pending: 0, reviewed: 0, resolved: 0 });
    }
  }, []);

  // Fetch feedback list
  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const response = await feedbackAPI.getAll(params);
      // Handle response properly - check if feedback is wrapped in data
      const feedbackData = response.data?.feedback || response.feedback || [];
      setFeedbackList(feedbackData);
      setError(null);
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError('Failed to load feedback. Please try again.');
      setFeedbackList([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchFeedback();
    fetchStats();
  }, [fetchFeedback, fetchStats]);

  // View feedback details
  const handleView = (feedback) => {
    setSelectedFeedback(feedback);
    setAdminNotes(feedback.adminNotes || '');
  };

  // Close detail view
  const handleCloseDetail = () => {
    setSelectedFeedback(null);
    setAdminNotes('');
  };

  // Update feedback status
  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await feedbackAPI.updateStatus(id, { 
        status: newStatus,
        adminNotes: adminNotes || null 
      });
      setSuccessMessage(`Feedback marked as ${newStatus}!`);
      fetchFeedback();
      fetchStats();
      handleCloseDetail();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating feedback:', err);
      setError('Failed to update feedback. Please try again.');
    }
  };

  // Delete feedback
  const handleDelete = async (id, subject) => {
    if (!window.confirm(`Are you sure you want to delete feedback: "${subject}"?`)) {
      return;
    }

    try {
      await feedbackAPI.delete(id);
      setSuccessMessage('Feedback deleted successfully!');
      fetchFeedback();
      fetchStats();
      handleCloseDetail();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting feedback:', err);
      setError('Failed to delete feedback. Please try again.');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>💬 Feedback Management</h2>
        <p className="section-subtitle">View and manage user feedback</p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="success-message">
          ✅ {successMessage}
        </div>
      )}
      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Feedback</div>
            </div>
          </div>
          <div className="stat-card pending">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <div className="stat-value">{stats.pending}</div>
              <div className="stat-label">Pending</div>
            </div>
          </div>
          <div className="stat-card reviewed">
            <div className="stat-icon">👁️</div>
            <div className="stat-content">
              <div className="stat-value">{stats.reviewed}</div>
              <div className="stat-label">Reviewed</div>
            </div>
          </div>
          <div className="stat-card resolved">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-value">{stats.resolved}</div>
              <div className="stat-label">Resolved</div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button 
          className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`}
          onClick={() => setFilterStatus('all')}
        >
          All Feedback
        </button>
        <button 
          className={`filter-tab ${filterStatus === 'pending' ? 'active' : ''}`}
          onClick={() => setFilterStatus('pending')}
        >
          Pending
        </button>
        <button 
          className={`filter-tab ${filterStatus === 'reviewed' ? 'active' : ''}`}
          onClick={() => setFilterStatus('reviewed')}
        >
          Reviewed
        </button>
        <button 
          className={`filter-tab ${filterStatus === 'resolved' ? 'active' : ''}`}
          onClick={() => setFilterStatus('resolved')}
        >
          Resolved
        </button>
      </div>

      {/* Feedback List */}
      <div className="admin-data-list">
        {loading ? (
          <p>Loading feedback...</p>
        ) : !feedbackList || feedbackList.length === 0 ? (
          <p className="empty-state">
            {filterStatus === 'all' 
              ? 'No feedback yet. Users can submit feedback from the dashboard header.'
              : `No ${filterStatus} feedback.`}
          </p>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>User</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {feedbackList.map(feedback => (
                  <tr key={feedback.id}>
                    <td className="title-cell">{feedback.subject}</td>
                    <td>
                      <div className="user-cell">
                        <div className="user-name">{feedback.userName}</div>
                        <div className="user-email">{feedback.userEmail}</div>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge status-${feedback.status}`}>
                        {feedback.status}
                      </span>
                    </td>
                    <td>{formatDate(feedback.createdAt)}</td>
                    <td className="actions-cell">
                      <button
                        onClick={() => handleView(feedback)}
                        className="btn-view"
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button
                        onClick={() => handleDelete(feedback.id, feedback.subject)}
                        className="btn-delete"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Feedback Detail Modal */}
      {selectedFeedback && (
        <div className="feedback-detail-modal">
          <div className="modal-overlay" onClick={handleCloseDetail}></div>
          <div className="modal-content">
            <div className="modal-header">
              <h3>Feedback Details</h3>
              <button className="close-btn" onClick={handleCloseDetail}>✕</button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <label>Subject:</label>
                <p className="detail-value">{selectedFeedback.subject}</p>
              </div>

              <div className="detail-section">
                <label>Submitted by:</label>
                <p className="detail-value">
                  {selectedFeedback.userName} ({selectedFeedback.userEmail})
                </p>
              </div>

              <div className="detail-section">
                <label>Submitted:</label>
                <p className="detail-value">{formatDate(selectedFeedback.createdAt)}</p>
              </div>

              <div className="detail-section">
                <label>Status:</label>
                <span className={`status-badge status-${selectedFeedback.status}`}>
                  {selectedFeedback.status}
                </span>
              </div>

              <div className="detail-section">
                <label>Message:</label>
                <div className="feedback-message">{selectedFeedback.message}</div>
              </div>

              {selectedFeedback.reviewerName && (
                <div className="detail-section">
                  <label>Reviewed by:</label>
                  <p className="detail-value">
                    {selectedFeedback.reviewerName} on {formatDate(selectedFeedback.reviewedAt)}
                  </p>
                </div>
              )}

              <div className="detail-section">
                <label>Admin Notes:</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this feedback..."
                  rows={4}
                  className="admin-notes-input"
                />
              </div>
            </div>

            <div className="modal-actions">
              {selectedFeedback.status !== 'reviewed' && (
                <button
                  onClick={() => handleUpdateStatus(selectedFeedback.id, 'reviewed')}
                  className="btn-status btn-reviewed"
                >
                  Mark as Reviewed
                </button>
              )}
              {selectedFeedback.status !== 'resolved' && (
                <button
                  onClick={() => handleUpdateStatus(selectedFeedback.id, 'resolved')}
                  className="btn-status btn-resolved"
                >
                  Mark as Resolved
                </button>
              )}
              {selectedFeedback.status !== 'pending' && (
                <button
                  onClick={() => handleUpdateStatus(selectedFeedback.id, 'pending')}
                  className="btn-status btn-pending"
                >
                  Mark as Pending
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeedbackManagement;
